import React from 'react'
import { InterchangeRailCar } from '../../../types/interchange'

interface ManifestTableProps {
  railCars: InterchangeRailCar[]
  onRowSelect?: (carId: string) => void
  selectedCarId?: string
  cutIndex?: number | null
  isCutActive?: boolean
  onCutClick?: (index: number) => void
}

const getStatusBadgeColor = (status?: InterchangeRailCar['status']) => {
  switch (status) {
    case 'INBOUND_ARRIVED':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    case 'CLASSIFIED':
    case 'RELEASED':
    case 'STAGED_FOR_DEPARTURE':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'BEING_SWITCHED':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
    case 'MAINTENANCE_HOLD':
    case 'LOCKED_VIOLATION':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    case 'IN_TRANSIT_OUTBOUND':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
  }
}

export default function ManifestTable({ railCars, onRowSelect, selectedCarId, cutIndex, isCutActive, onCutClick }: ManifestTableProps) {
  if (railCars.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        Planning complete — all cars assigned across passes. To dispatch, proceed to 'Dispatch Work Order' or 'Reset Plan' to modify
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 px-3 font-semibold text-foreground">Seq</th>
            <th className="text-left py-2 px-3 font-semibold text-foreground">Car ID</th>
            <th className="text-left py-2 px-3 font-semibold text-foreground">Type</th>
            <th className="text-left py-2 px-3 font-semibold text-foreground">Commodity</th>
            <th className="text-left py-2 px-3 font-semibold text-foreground">Length</th>
            <th className="text-left py-2 px-3 font-semibold text-foreground">Status</th>
          </tr>
        </thead>
        <tbody>
          {railCars.map((car, index) => {
            const isPendingBlock = isCutActive && index >= (cutIndex || railCars.length)
            
            return (
              <React.Fragment key={car.id}>
                {/* Interactive seam — faint but persistent: touch has no
                    hover, so the affordance must be visible at rest. */}
                {index > 0 && onCutClick && (
                  <tr
                    className="border-b border-transparent hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer group"
                    onClick={() => onCutClick(index)}
                  >
                    <td colSpan={6} className="py-1 px-3 pointer-coarse:py-2">
                      <div className="relative h-1 pointer-coarse:h-2 flex items-center justify-center">
                        <div className={`absolute inset-0 border-t-2 border-red-500 transition-opacity ${isCutActive && index === cutIndex ? 'border-solid opacity-100' : 'border-dashed opacity-40 group-hover:opacity-100'}`}></div>
                        <div className={`relative bg-background px-2 text-xs text-red-500 transition-opacity ${isCutActive && index === cutIndex ? 'opacity-100 font-medium' : 'opacity-60 group-hover:opacity-100'}`}>
                          {isCutActive && index === cutIndex ? (
                            '✂ Cut placed here'
                          ) : (
                            <>
                              <span className="pointer-coarse:hidden">✂ Click to cut here</span>
                              <span className="hidden pointer-coarse:inline">✂ Tap to cut here</span>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                <tr
                  className={`border-b hover:bg-muted/50 cursor-pointer ${
                    selectedCarId === car.id ? 'bg-muted' : ''
                  } ${isPendingBlock ? 'bg-muted/30 opacity-60' : ''}`}
                  onClick={() => onRowSelect?.(car.id)}
                >
                  <td className="py-2 px-3 text-muted-foreground">{car.sequenceNumber}</td>
                  <td className="py-2 px-3 font-mono text-foreground">{car.reportingMark}</td>
                  <td className="py-2 px-3 text-foreground">{car.assetType}</td>
                  <td className="py-2 px-3 text-foreground">{car.commodity || '-'}</td>
                  <td className="py-2 px-3 text-foreground">{car.lengthFeet}ft</td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(car.status)}`}>
                      {car.status}
                    </span>
                    {isPendingBlock && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        ⏳ PENDING
                      </span>
                    )}
                  </td>
                </tr>
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
